// PST (Aug-2026) — Project-to-Project stock transfer: raise → reserve →
// destination user accepts (stock moves) / rejects with reason; requester
// can cancel while Pending. Values masked without project_stock_values.
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { RefreshCw, Plus, X, ArrowRightLeft, Check, Ban, Trash2 } from "lucide-react";
import { SearchableSelect } from "@/components/SearchableSelect";

const inputCls = "border border-slate-300 rounded px-2 py-1.5 text-xs";

const STATUS_CLS = {
  Pending: "bg-amber-100 text-amber-800",
  Accepted: "bg-emerald-100 text-emerald-800",
  Rejected: "bg-rose-100 text-rose-800",
  Cancelled: "bg-slate-200 text-slate-600",
};

function StatusBadge({ s }) {
  return (
    <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold whitespace-nowrap ${STATUS_CLS[s] || "bg-orange-100 text-orange-800"}`}>
      {s}
    </span>
  );
}

function LinesCell({ t, valuesVisible }) {
  return (
    <div className="space-y-0.5">
      {(t.line_items || []).map((l) => (
        <div key={l.line_uid} className="whitespace-nowrap">
          {l.item_name} <span className="text-slate-400 font-mono">{l.item_code}</span>
          {" — "}<b>{l.qty}</b> {l.uom}
          {valuesVisible && l.provisional_value !== undefined && (
            <span className="text-slate-400"> · ₹{Number(l.provisional_value).toLocaleString("en-IN")}</span>
          )}
          <div className="text-[10px] text-slate-400 font-mono">{l.batch_code}</div>
        </div>
      ))}
    </div>
  );
}

function NewTransferModal({ project, onClose, onDone }) {
  const [targets, setTargets] = useState([]);
  const [stock, setStock] = useState([]);
  const [toId, setToId] = useState("");
  const [remarks, setRemarks] = useState("");
  const [lines, setLines] = useState([{ batch_id: "", qty: "" }]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [t, s] = await Promise.all([
          api.get(`/projects/${project.id}/stock-transfer-targets`),
          api.get(`/projects/${project.id}/transferable-stock`)]);
        setTargets(t.data || []);
        setStock(s.data?.rows || []);
      } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load transfer data"); }
    })();
  }, [project.id]);

  const setLine = (i, patch) => setLines(lines.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  const batchOf = (id) => stock.find((s) => s.batch_id === id);

  const submit = async () => {
    if (!toId) return toast.error("Select the destination project");
    const clean = lines.filter((l) => l.batch_id && Number(l.qty) > 0);
    if (!clean.length) return toast.error("Add at least one line with batch and quantity");
    setBusy(true);
    try {
      const r = await api.post(`/projects/${project.id}/stock-transfers`, {
        to_project_id: toId, remarks,
        line_items: clean.map((l) => ({ batch_id: l.batch_id, qty: Number(l.qty) })) });
      toast.success(`Transfer ${r.data.transfer_number} raised — stock reserved, awaiting acceptance`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to raise transfer"); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center pt-16 px-4" data-testid="pst-new-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl p-4 space-y-3 max-h-[80vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="font-black text-sm inline-flex items-center gap-2"><ArrowRightLeft size={14} /> New Stock Transfer Request</h3>
          <button data-testid="pst-new-close" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="text-[11px] bg-amber-50 border border-amber-200 text-amber-800 rounded p-2">
          Requested quantities are <b>reserved</b> on this project's stock until the destination accepts, rejects, or you cancel.
        </div>
        <div>
          <label className="text-[10px] font-bold uppercase text-slate-500">Destination Project *</label>
          <SearchableSelect testid="pst-target-select" className="w-full"
            items={targets} value={toId} onChange={(id) => setToId(id)}
            getLabel={(t) => `${t.project_code} — ${t.project_name}`}
            getSubLabel={(t) => t.branch_name}
            getSearchText={(t) => `${t.project_code} ${t.project_name} ${t.branch_name}`}
            placeholder="— select operational project —"
            searchPlaceholder="Search project code / name / branch…" />
        </div>
        <div className="space-y-2">
          <label className="text-[10px] font-bold uppercase text-slate-500">Items (available = free of consumption & reservations)</label>
          {lines.map((l, i) => {
            const b = batchOf(l.batch_id);
            return (
              <div key={i} className="flex items-center gap-2">
                <SearchableSelect testid={`pst-line-batch-${i}`} className="flex-1"
                  items={stock} value={l.batch_id}
                  onChange={(id) => setLine(i, { batch_id: id })}
                  getKey={(s) => s.batch_id}
                  getLabel={(s) => s.item_name}
                  getSubLabel={(s) => `${s.batch_code} · free ${s.free_qty} ${s.uom}`}
                  getSearchText={(s) => `${s.item_name} ${s.item_code} ${s.batch_code} ${s.grn_number} ${s.origin_transfer_number}`}
                  placeholder="— select item / batch —"
                  searchPlaceholder="Search item name / code / batch / GRN…" />
                <input data-testid={`pst-line-qty-${i}`} type="number" min="0" step="any" className={`${inputCls} w-28`}
                  placeholder={b ? `≤ ${b.free_qty}` : "Qty"} value={l.qty}
                  onChange={(e) => setLine(i, { qty: e.target.value })} />
                <span className="text-[10px] text-slate-400 w-10">{b?.uom || ""}</span>
                {lines.length > 1 && (
                  <button data-testid={`pst-line-remove-${i}`} onClick={() => setLines(lines.filter((_, j) => j !== i))}
                    className="text-rose-600"><Trash2 size={13} /></button>
                )}
              </div>
            );
          })}
          <button data-testid="pst-add-line" onClick={() => setLines([...lines, { batch_id: "", qty: "" }])}
            className="text-emerald-700 text-xs font-bold inline-flex items-center gap-1"><Plus size={12} /> Add line</button>
        </div>
        <div>
          <label className="text-[10px] font-bold uppercase text-slate-500">Remarks</label>
          <textarea data-testid="pst-remarks" className={`${inputCls} w-full`} rows={2} value={remarks}
            onChange={(e) => setRemarks(e.target.value)} placeholder="Why is this material being transferred?" />
        </div>
        <div className="flex justify-end gap-2">
          <button data-testid="pst-new-cancel" onClick={onClose} className="px-3 py-1.5 text-xs border rounded">Cancel</button>
          <button data-testid="pst-new-submit" onClick={submit} disabled={busy}
            className="px-4 py-1.5 text-xs font-bold bg-emerald-700 text-white rounded disabled:opacity-50">
            {busy ? "Raising…" : "Raise Transfer Request"}
          </button>
        </div>
      </div>
    </div>
  );
}

export function ProjectStockTransfers({ project, onStockChanged }) {
  const pid = project.id;
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [openNew, setOpenNew] = useState(false);
  const [reject, setReject] = useState(null);       // {t, reason}
  const [busyId, setBusyId] = useState("");

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/projects/${pid}/stock-transfers`, {
        params: { q: q || undefined, page, limit: 25 } });
      setData(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load transfers"); }
  }, [pid, q, page]);

  useEffect(() => { load(); }, [load]);

  const act = async (t, action, body) => {
    setBusyId(t.id);
    try {
      const path = action === "cancel"
        ? `/projects/${t.from_project_id}/stock-transfers/${t.id}/cancel`
        : `/projects/${t.to_project_id}/stock-transfers/${t.id}/${action}`;
      await api.post(path, body || {});
      toast.success(`Transfer ${t.transfer_number} ${action === "recover" ? "recovered" : action + "ed"}`);
      setReject(null);
      load();
      onStockChanged && onStockChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || `Failed to ${action}`); }
    setBusyId("");
  };

  if (!data) return <div className="text-xs text-slate-400 py-6">Loading transfers…</div>;
  const rows = data.rows || [];
  const pages = Math.max(1, Math.ceil((data.total || 0) / 25));

  // Shared row actions — desktop table cell AND mobile card (m = testid infix).
  const transferActions = (t, incoming, mine, m = "") => (
    <>
      {incoming && t.status === "Pending" && !mine && (
        <>
          <button data-testid={`pst-accept-${m}${t.transfer_number}`} disabled={busyId === t.id}
            onClick={() => act(t, "accept")}
            className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-bold bg-emerald-700 text-white rounded disabled:opacity-50">
            <Check size={11} /> Accept
          </button>
          <button data-testid={`pst-reject-${m}${t.transfer_number}`} disabled={busyId === t.id}
            onClick={() => setReject({ t, reason: "" })}
            className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-bold border border-rose-300 text-rose-700 rounded disabled:opacity-50">
            <Ban size={11} /> Reject
          </button>
        </>
      )}
      {incoming && t.status === "Pending" && mine && (
        <span className="text-[10px] text-slate-400 italic">awaiting another user</span>
      )}
      {incoming && (t.status === "Accepting" || t.status.startsWith("Accept Failed")) && (
        <button data-testid={`pst-recover-${m}${t.transfer_number}`} disabled={busyId === t.id}
          onClick={() => act(t, "recover")}
          className="px-2 py-1 text-[11px] font-bold border border-orange-300 text-orange-700 rounded disabled:opacity-50">
          Retry Accept
        </button>
      )}
      {!incoming && t.status === "Pending" && (
        <button data-testid={`pst-cancel-${m}${t.transfer_number}`} disabled={busyId === t.id}
          onClick={() => act(t, "cancel")}
          className="px-2 py-1 text-[11px] font-bold border border-slate-300 text-slate-600 rounded disabled:opacity-50">
          Cancel
        </button>
      )}
    </>
  );

  return (
    <div className="space-y-3" data-testid="pst-section">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <input data-testid="pst-search" className={`${inputCls} w-full sm:w-64`} placeholder="Search number / item / project / status…"
            value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} />
          <button data-testid="pst-refresh" onClick={load} className="p-1.5 text-slate-500 hover:text-slate-800"><RefreshCw size={14} /></button>
        </div>
        {data.can_create && (
          <button data-testid="pst-new-btn" onClick={() => setOpenNew(true)}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold bg-emerald-700 text-white rounded">
            <Plus size={13} /> New Transfer Request
          </button>
        )}
      </div>

      <div className="hidden md:block border border-slate-200 rounded overflow-x-auto">
        <table className="w-full min-w-[880px] text-xs" data-testid="pst-table">
          <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
            <tr>
              <th className="px-3 py-2 text-left">Transfer #</th>
              <th className="px-3 py-2 text-left">Direction</th>
              <th className="px-3 py-2 text-left">Items</th>
              <th className="px-3 py-2 text-left">Requested By</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-left">Remarks</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-8 text-center text-slate-400" data-testid="pst-empty">
                No stock transfers yet. {data.can_create ? "Raise one to move material to another operational project." : ""}
              </td></tr>
            )}
            {rows.map((t) => {
              const incoming = t.to_project_id === pid;
              const mine = t.requested_by_id === data.user_id;
              return (
                <tr key={t.id} className="border-t border-slate-100 align-top hover:bg-slate-50" data-testid={`pst-row-${t.transfer_number}`}>
                  <td className="px-3 py-2 font-mono whitespace-nowrap">{t.transfer_number}
                    <div className="text-slate-400">{t.transfer_date}</div></td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {incoming ? (
                      <span className="text-sky-800 font-bold">IN ← {t.from_project_code}</span>
                    ) : (
                      <span className="text-slate-700 font-bold">OUT → {t.to_project_code}</span>
                    )}
                  </td>
                  <td className="px-3 py-2"><LinesCell t={t} valuesVisible={data.values_visible} /></td>
                  <td className="px-3 py-2">{t.requested_by}</td>
                  <td className="px-3 py-2">
                    <StatusBadge s={t.status} />
                    {t.status === "Rejected" && t.rejection_reason && (
                      <div className="text-[10px] text-rose-600 mt-0.5 max-w-[180px]">{t.rejection_reason}</div>
                    )}
                    {t.accepted_by && <div className="text-[10px] text-slate-400 mt-0.5">by {t.accepted_by}</div>}
                  </td>
                  <td className="px-3 py-2 max-w-[180px]">{t.remarks}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap space-x-2">{transferActions(t, incoming, mine)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile: stacked transfer cards */}
      <div className="md:hidden border border-slate-200 rounded divide-y divide-slate-100" data-testid="pst-cards">
        {rows.length === 0 && (
          <div className="px-3 py-6 text-center text-slate-400 text-xs">
            No stock transfers yet. {data.can_create ? "Raise one to move material to another operational project." : ""}
          </div>
        )}
        {rows.map((t) => {
          const incoming = t.to_project_id === pid;
          const mine = t.requested_by_id === data.user_id;
          return (
            <div key={t.id} className="p-3 space-y-1.5" data-testid={`pst-card-${t.transfer_number}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-[11px] font-bold">{t.transfer_number}</span>
                <StatusBadge s={t.status} />
                <span className="ml-auto text-[10px] text-slate-400">{t.transfer_date}</span>
              </div>
              <div className="text-xs font-bold">
                {incoming
                  ? <span className="text-sky-800">IN ← {t.from_project_code}</span>
                  : <span className="text-slate-700">OUT → {t.to_project_code}</span>}
              </div>
              <div className="text-[11px]"><LinesCell t={t} valuesVisible={data.values_visible} /></div>
              {t.status === "Rejected" && t.rejection_reason && (
                <div className="text-[10px] text-rose-600 break-words">{t.rejection_reason}</div>
              )}
              <div className="text-[10px] text-slate-500 break-all">
                By {t.requested_by}{t.accepted_by ? ` · accepted by ${t.accepted_by}` : ""}
              </div>
              {t.remarks && <div className="text-[10px] text-slate-500 break-words">{t.remarks}</div>}
              <div className="flex flex-wrap items-center gap-2 pt-0.5">{transferActions(t, incoming, mine, "m-")}</div>
            </div>
          );
        })}
      </div>
      {pages > 1 && (
        <div className="flex items-center justify-end gap-2 text-xs">
          <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="px-2 py-1 border rounded disabled:opacity-40">Prev</button>
          <span>Page {page} / {pages}</span>
          <button disabled={page >= pages} onClick={() => setPage(page + 1)} className="px-2 py-1 border rounded disabled:opacity-40">Next</button>
        </div>
      )}

      {openNew && (
        <NewTransferModal project={project} onClose={() => setOpenNew(false)}
          onDone={() => { setOpenNew(false); load(); onStockChanged && onStockChanged(); }} />
      )}
      {reject && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center pt-24 px-4" data-testid="pst-reject-modal">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-4 space-y-3">
            <h3 className="font-black text-sm">Reject {reject.t.transfer_number}</h3>
            <textarea data-testid="pst-reject-reason" className={`${inputCls} w-full`} rows={3} autoFocus
              placeholder="Rejection reason (mandatory)" value={reject.reason}
              onChange={(e) => setReject({ ...reject, reason: e.target.value })} />
            <div className="flex justify-end gap-2">
              <button data-testid="pst-reject-cancel" onClick={() => setReject(null)} className="px-3 py-1.5 text-xs border rounded">Back</button>
              <button data-testid="pst-reject-confirm" disabled={!reject.reason.trim() || busyId === reject.t.id}
                onClick={() => act(reject.t, "reject", { reason: reject.reason.trim() })}
                className="px-4 py-1.5 text-xs font-bold bg-rose-600 text-white rounded disabled:opacity-50">
                Reject Transfer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
